import { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import moment from 'moment'

import FeatureToggle from '~/constants/featureToggle'
import { getFeatureToggle } from '~/store/reducers/constants'

interface TimetableContextProps {
  clearScrollPosition: () => void
  saveScrollPosition: (position: number) => void
  savedScrollPosition: number | null
  selectedDate: string | null
  setSelectedDate: (newDate: string) => void
}

const TimetableContext = createContext({} as TimetableContextProps)

const TimetableContextProvider = ({ children }: { children: JSX.Element }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const queryParams = new URLSearchParams(location.search)

  const isPreventScrollResetOnSelectedDateChangeEnabled = useSelector(
    getFeatureToggle(
      FeatureToggle.PREVENT_SCROLL_RESET_ON_SELECTED_DATE_CHANGE,
    ),
  )

  const [selectedDate, setSelectedDate] = useState<string | null>(
    queryParams.get('date'),
  )
  const [savedScrollPosition, setSavedScrollPosition] = useState<number | null>(
    null,
  )

  const setDate = useCallback(
    (newDate: string) => {
      const newQueryParams = new URLSearchParams(location.search)
      setSelectedDate(newDate)

      newQueryParams.set('date', newDate)
      navigate(`${location.pathname}?${newQueryParams.toString()}`, {
        replace: true,
      })
    },
    [location.search],
  )

  const saveScrollPosition = useCallback(
    (position: number) => {
      if (isPreventScrollResetOnSelectedDateChangeEnabled) {
        setSavedScrollPosition(position)
      }
    },
    [isPreventScrollResetOnSelectedDateChangeEnabled],
  )

  const clearScrollPosition = useCallback(() => {
    if (isPreventScrollResetOnSelectedDateChangeEnabled) {
      setSavedScrollPosition(null)
    }
  }, [isPreventScrollResetOnSelectedDateChangeEnabled])

  useEffect(() => {
    if (!selectedDate) {
      setDate(moment().format('YYYY-MM-DD'))
    }
  }, [selectedDate, location.pathname])

  const contextValue = useMemo(
    () => ({
      selectedDate,
      setSelectedDate: setDate,
      savedScrollPosition: isPreventScrollResetOnSelectedDateChangeEnabled
        ? savedScrollPosition
        : null,
      saveScrollPosition,
      clearScrollPosition,
    }),
    [
      selectedDate,
      setDate,
      savedScrollPosition,
      saveScrollPosition,
      clearScrollPosition,
      isPreventScrollResetOnSelectedDateChangeEnabled,
    ],
  )

  return (
    <TimetableContext.Provider value={contextValue}>
      {children}
    </TimetableContext.Provider>
  )
}

export { TimetableContext, TimetableContextProvider }
